import React, { useState, useEffect } from 'react';
import { getJerarquiaPorProducto, ProductoResumen, getContratantes, Contratante } from '../services/horasService';
import DetalleHorasContrato from './DetalleHorasContrato';
import FiltrosFecha, { FiltrosFechaType } from './FiltrosFecha';

const HorasPorProducto: React.FC = () => {
  const [productosResumen, setProductosResumen] = useState<ProductoResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProductos, setExpandedProductos] = useState<Set<string>>(new Set());
  const [expandedCategorias, setExpandedCategorias] = useState<Set<string>>(new Set());
  const [expandedClientes, setExpandedClientes] = useState<Set<string>>(new Set());
  const [filtroActivo, setFiltroActivo] = useState(true);
  const [showDetalle, setShowDetalle] = useState(false);
  const [contratoSeleccionado, setContratoSeleccionado] = useState<string>('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState<string>('');
  const [fechaInicioSeleccionada, setFechaInicioSeleccionada] = useState<string | undefined>();
  const [fechaFinSeleccionada, setFechaFinSeleccionada] = useState<string | undefined>();
  const [horasContratadasSeleccionada, setHorasContratadasSeleccionada] = useState<number | undefined>();
  const [contratantes, setContratantes] = useState<Contratante[]>([]);
  const [contratanteSeleccionado, setContratanteSeleccionado] = useState<string>('');
  const [filtrosFecha, setFiltrosFecha] = useState<FiltrosFechaType>({ periodo: 'mes_actual' });
  const [filtroSoporte, setFiltroSoporte] = useState<'todos' | 'solo_soporte' | 'solo_proyectos'>('solo_soporte');

  const fetchContratantes = async () => {
    try {
      const data = await getContratantes();
      setContratantes(data);
    } catch (err) {
      setContratantes([]);
    }
  };

  const fetchHoras = async (filtro?: string, contratante?: string, filtrosFecha?: FiltrosFechaType) => {
    try {
      setLoading(true);
      const data = await getJerarquiaPorProducto(
        undefined, 
        contratante, 
        filtrosFecha?.fechaDesde, 
        filtrosFecha?.fechaHasta, 
        filtrosFecha?.periodo
      );
      setProductosResumen(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos de horas por producto');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContratantes();
    fetchHoras(undefined, undefined, filtrosFecha);
  }, []);

  useEffect(() => {
    fetchHoras(undefined, contratanteSeleccionado || undefined, filtrosFecha);
    // eslint-disable-next-line
  }, [contratanteSeleccionado, filtrosFecha]);

  const handleContratanteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setContratanteSeleccionado(e.target.value);
  };

  const toggleFiltro = () => {
    const nuevoFiltro = !filtroActivo;
    setFiltroActivo(nuevoFiltro);
    fetchHoras(nuevoFiltro ? 'SOPORTE' : undefined, contratanteSeleccionado || undefined, filtrosFecha);
  };

  const handleFiltrosFechaChange = (nuevosFiltros: FiltrosFechaType) => {
    setFiltrosFecha(nuevosFiltros);
  };

  const handleFiltroSoporte = (tipo: 'todos' | 'solo_soporte' | 'solo_proyectos') => {
    setFiltroSoporte(tipo);
  };

  const toggleProducto = (producto: string) => {
    const newExpanded = new Set(expandedProductos);
    if (newExpanded.has(producto)) {
      newExpanded.delete(producto);
    } else {
      newExpanded.add(producto);
    }
    setExpandedProductos(newExpanded);
  };





  const abrirDetalle = (contrato: string, fechaInicio?: string, fechaFin?: string, horasContratadas?: number) => {
    setContratoSeleccionado(contrato);
    setFechaInicioSeleccionada(fechaInicio);
    setFechaFinSeleccionada(fechaFin);
    setHorasContratadasSeleccionada(horasContratadas);
    setShowDetalle(true);
    
    // Debug: Verificar las fechas recibidas
    console.log('HorasPorProducto - Contrato seleccionado:', contrato);
    console.log('HorasPorProducto - fechaInicio recibida:', fechaInicio);
    console.log('HorasPorProducto - fechaFin recibida:', fechaFin);
    console.log('HorasPorProducto - horasContratadas recibida:', horasContratadas);
  };

  const cerrarDetalle = () => {
    setShowDetalle(false);
    setContratoSeleccionado('');
    setFechaInicioSeleccionada(undefined);
    setFechaFinSeleccionada(undefined);
    setHorasContratadasSeleccionada(undefined);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Excedido':
        return '#ff6b6b';
      case 'Completado':
        return '#51cf66';
      case 'En Progreso':
        return '#339af0';
      default:
        return '#868e96';
    }
  };



  const totalHorasConsumidas = productosResumen.reduce((sum, producto) => sum + producto.totalHorasAplicadas, 0);
  const totalHorasContratadas = productosResumen.reduce((sum, producto) => sum + producto.totalHorasContratadas, 0);
  const porcentajeTotal = totalHorasContratadas > 0 ? (totalHorasConsumidas / totalHorasContratadas) * 100 : 0;

  const productosFiltrados = productosResumen.map(producto => {
    const categoriasFiltradas = producto.categorias.map(categoria => {
      const clientesFiltrados = categoria.clientes.map(cliente => {
        const contratosFiltrados = cliente.contratos.filter(contrato => {
          const nombre = contrato.contrato.toLowerCase();
          if (filtroSoporte === 'solo_soporte') {
            return nombre.includes('soporte');
          } else if (filtroSoporte === 'solo_proyectos') {
            return !nombre.includes('soporte');
          } else {
            return true;
          }
        });
        return { ...cliente, contratos: contratosFiltrados };
      }).filter(cliente => cliente.contratos.length > 0);
      return { ...categoria, clientes: clientesFiltrados };
    }).filter(categoria => categoria.clientes.length > 0);
    return { ...producto, categorias: categoriasFiltradas };
  }).filter(producto => producto.categorias.length > 0);

  if (loading) {
    return <div className="loading">Cargando datos de horas por producto...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="horas-container">
      <div className="header-section">
        <FiltrosFecha 
          onFiltroChange={handleFiltrosFechaChange}
          filtroActual={filtrosFecha.periodo}
        />
        
        <div className="filtros-row">
          <div className="filtro-section">
            <button 
              className={`filtro-button ${filtroSoporte === 'todos' ? 'activo' : ''}`}
              onClick={() => handleFiltroSoporte('todos')}
            >
              Mostrar Todos
            </button>
            <button 
              className={`filtro-button ${filtroSoporte === 'solo_soporte' ? 'activo' : ''}`}
              onClick={() => handleFiltroSoporte('solo_soporte')}
            >
              Solo SOPORTE
            </button>
            <button 
              className={`filtro-button ${filtroSoporte === 'solo_proyectos' ? 'activo' : ''}`}
              onClick={() => handleFiltroSoporte('solo_proyectos')}
            >
              Solo Proyectos
            </button>
            <span className="filtro-indicator">
              {filtroSoporte === 'solo_soporte' && 'Filtrado: Solo contratos con "SOPORTE"'}
              {filtroSoporte === 'solo_proyectos' && 'Filtrado: Solo contratos SIN "SOPORTE"'}
              {filtroSoporte === 'todos' && 'Mostrando todos los contratos'}
            </span>
          </div>
          {/* <div className="contratante-section">
            <label htmlFor="contratante-select">Contratante:</label>
            <select
              id="contratante-select"
              value={contratanteSeleccionado}
              onChange={handleContratanteChange}
            >
              <option value="">Todos</option>
              {contratantes.map((c, idx) => (
                <option key={idx} value={c.contratante}>{c.contratante}</option>
              ))}
            </select>
          </div> */}
        </div>
      </div>
      
      <div className="productos-grid">
        {productosFiltrados.map((producto, index) => {
          // Verificar si el producto tiene contratos excedidos
          const tieneContratosExcedidos = producto.categorias.some(categoria => 
            categoria.clientes.some(cliente => 
              cliente.contratos.some(contrato => contrato.estado === 'Excedido')
            )
          );
          // Unificar todos los contratos del producto
          const contratos = producto.categorias.flatMap(categoria =>
            categoria.clientes.flatMap(cliente => cliente.contratos)
          );
          return (
            <div 
              key={index} 
              className={`producto-card ${tieneContratosExcedidos ? 'con-contratos-excedidos' : ''}`}
            >
              <div className="producto-header" onClick={() => toggleProducto(producto.producto)}>
                <div className="producto-info">
                  <div className="producto-nombre">🏭 {producto.producto}</div>
                  <div className="producto-resumen">
                    <span className="resumen-item">
                      <span className="resumen-label">Total Consumidas:</span>
                      <span className="resumen-valor-consumidas">{producto.totalHorasAplicadas.toFixed(2)}h</span>
                    </span>
                    <span className="resumen-item">
                      <span className="resumen-label">Total Contratadas:</span>
                      <span className="resumen-valor-contratadas">{producto.totalHorasContratadas.toFixed(2)}h</span>
                    </span>
                    <span className="resumen-item">
                      <span className="resumen-label">Utilización:</span>
                      <span className="resumen-valor-utilizacion">{producto.porcentajeUtilizacionGeneral.toFixed(1)}%</span>
                    </span>
                  </div>
                </div>
                <div className="expand-button">
                  {expandedProductos.has(producto.producto) ? '−' : '+'}
                </div>
              </div>
              {expandedProductos.has(producto.producto) && (
                <div className="contratos-section">
                  <h4>Contratos</h4>
                  <div className="contratos-grid">
                    {contratos.map((contrato, contratoIndex) => (
                      <div
                        key={contratoIndex}
                        className={`contrato-card ${contrato.estado === 'Excedido' ? 'excedido' : ''}`}
                      >
                        <div className="contrato-nombre">
                          <span className="contrato-label">📋</span> {contrato.contrato}
                        </div>
                        <div className="contrato-detalles">
                          <div className="detalle-row">
                            <span className="detalle-label">Horas Aplicadas:</span>
                            <span className="detalle-valor-aplicadas">{contrato.horasAplicadas.toFixed(2)}h</span>
                          </div>
                          <div className="detalle-row">
                            <span className="detalle-label">Horas Contratadas:</span>
                            <span className="detalle-valor-contratadas">{contrato.horasContratadas.toFixed(2)}h</span>
                          </div>
                          <div className="detalle-row">
                            <span className="detalle-label">Utilización:</span>
                            <span className="detalle-valor-utilizacion">{contrato.porcentajeUtilizacion.toFixed(1)}%</span>
                          </div>
                          <div className="detalle-row">
                            <span className="detalle-label">Vigencia:</span>
                            <span className="detalle-valor-fechas">
                              {contrato.fechaInicio ? new Date(contrato.fechaInicio).toLocaleDateString('es-ES') : 'N/A'} - {contrato.fechaFin ? new Date(contrato.fechaFin).toLocaleDateString('es-ES') : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="contrato-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div 
                            className="estado-badge" 
                            style={{ backgroundColor: getEstadoColor(contrato.estado) }}
                          >
                            {contrato.estado}
                          </div>
                          <button 
                            className="ver-detalle-button"
                            onClick={() => {
                              console.log('HorasPorProducto - Botón clickeado para contrato:', contrato.contrato);
                              console.log('HorasPorProducto - Fechas del contrato:', contrato.fechaInicio, contrato.fechaFin);
                              console.log('HorasPorProducto - Horas contratadas del contrato:', contrato.horasContratadas);
                              abrirDetalle(contrato.contrato, contrato.fechaInicio, contrato.fechaFin, contrato.horasContratadas);
                            }}
                          >
                            Ver Detalle
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="total-section">
        <h3>Resumen General del Mes</h3>
        <div className="total-grid">
          <div className="total-item">
            <div className="total-label">Total Horas Consumidas</div>
            <div className="total-valor">{totalHorasConsumidas.toFixed(2)} horas</div>
          </div>
          <div className="total-item">
            <div className="total-label">Total Horas Contratadas</div>
            <div className="total-valor">{totalHorasContratadas.toFixed(2)} horas</div>
          </div>
          <div className="total-item">
            <div className="total-label">Utilización General</div>
            <div className="total-valor">{porcentajeTotal.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      <div className="exclusion-info">
        <span className="exclusion-icon">⚠️</span>
        <span className="exclusion-text">
          Se excluyen tickets: Rechazado, Anulado, Duplicado, Análisis Comercial, Sin Solución
        </span>
      </div>
      <div className="exclusion-info motivos">
        <span className="exclusion-icon">🚫</span>
        <span className="exclusion-text">
          Se excluyen motivos: Incidente de Implementación, Ola de Actualización
        </span>
      </div>
      <div className="exclusion-info conceptos">
        <span className="exclusion-icon">🛈</span>
        <span className="exclusion-text">
          Se excluyen conceptos que comienzan con <b>"Z"</b> (no descuentan horas de contrato)
        </span>
      </div>
      <div className="exclusion-info excepcion">
        <span className="exclusion-icon">ℹ️</span>
        <span className="exclusion-text">
          <strong>Excepción:</strong> Planes de Soporte: Para las categorías "Neos ONE", "Glowix" y "GestionApp", el motivo "Error de Software" no son incluidas en el descuento de horas
        </span>
      </div>

      {showDetalle && (
        <DetalleHorasContrato
          contrato={contratoSeleccionado}
          fechaInicio={fechaInicioSeleccionada ? new Date(fechaInicioSeleccionada) : undefined}
          fechaFin={fechaFinSeleccionada ? new Date(fechaFinSeleccionada) : undefined}
          cliente={clienteSeleccionado}
          horasContratadas={horasContratadasSeleccionada}
          onClose={cerrarDetalle}
        />
      )}
    </div>
  );
};

export default HorasPorProducto; 